import React, { useState, useMemo } from 'react';
import MeetingBookingModal from '../modals/MeetingBookingModal';

/**
 * Genererar tillgängliga tidsslottar för hembesök
 * Vardagar (mån-fre) kl 09:00, 11:00, 14:00 de kommande 3 veckorna
 */
const generateAvailableSlots = () => {
  const slots = [];
  const now = new Date();
  const weekdays = ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'];
  const months = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
  const times = ['09:00', '11:00', '14:00'];
  
  // Gå igenom de kommande 21 dagarna
  for (let i = 1; i <= 21; i++) {
    const date = new Date(now);
    date.setDate(now.getDate() + i);
    
    const dayOfWeek = date.getDay();
    // Hoppa över helger (0 = söndag, 6 = lördag)
    if (dayOfWeek === 0 || dayOfWeek === 6) continue;
    
    const dayName = weekdays[dayOfWeek];
    const dayNum = date.getDate();
    const month = months[date.getMonth()];
    
    times.forEach(time => {
      slots.push(`${dayName} ${dayNum} ${month} kl ${time}`);
    });
  }
  
  return slots;
};

/**
 * CustomerHomeVisitRequest - Låter kunden föreslå tider för hembesök
 * Visas i EstimatView när estimat är godkänt och hembesök inte är bokat/föreslaget
 */
const CustomerHomeVisitRequest = ({
  procurementData,
  onSubmitProposals,
  submitting = false,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  // Generera tillgängliga slots (memoized)
  const availableSlots = useMemo(() => generateAvailableSlots(), []);

  const homeVisitWorkflow = procurementData?.homeVisitWorkflow;
  const workflowStatus = homeVisitWorkflow?.status;

  // Hembesök är redan bokat eller genomfört - dölj helt
  const hasBookedHomeVisit = workflowStatus === 'booked' ||
                              workflowStatus === 'completed' ||
                              Boolean(homeVisitWorkflow?.booking?.bookedAt);

  // Byggkonsulten har redan skickat tidsförslag - dölj helt
  const consultantHasProposed = workflowStatus === 'proposals_sent';

  // Kunden har redan svarat på byggkonsultens förslag (accepterat en tid)
  const customerHasResponded = workflowStatus === 'customer_responded';

  // Kunden har redan skickat förslag
  const customerHasProposed = workflowStatus === 'customer_proposals_sent';

  // Dölj helt om hembesök är bokat, byggkonsulten föreslagit tider, eller kunden accepterat en tid
  if (hasBookedHomeVisit || consultantHasProposed || customerHasResponded) {
    return null;
  }

  // Om kunden redan har skickat förslag, visa bekräftelse
  if (customerHasProposed || submitted) {
    return (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-md font-semibold text-purple-900">
              Hembesöksförslag skickat
            </h3>
            <p className="text-sm text-purple-700 mt-1">
              Dina tidsförslag har skickats till byggkonsulten. Du får besked när en tid är bekräftad.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSubmitSlots = async (slots) => {
    if (slots.length >= 2 && onSubmitProposals) {
      try {
        await onSubmitProposals(slots, '');
        setSubmitted(true);
        setShowModal(false);
      } catch (error) {
        console.error('Kunde inte skicka hembesöksförslag:', error);
      }
    }
  };

  const handleSlotChange = (slots) => {
    setSelectedSlots(slots);
  };

  return (
    <>
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-md font-semibold text-purple-900">
              Boka hembesök
            </h3>
            <p className="text-sm text-purple-700 mt-1">
              Nästa steg är ett hembesök där byggkonsulten kommer hem till dig för att gå igenom projektet på plats. 
              Föreslå tider som passar dig så återkommer vi med bekräftelse.
            </p>
            
            {selectedSlots.length > 0 && !showModal && (
              <div className="mt-3 p-2 bg-purple-100 border border-purple-300 rounded text-sm text-purple-800">
                <strong>Föreslagna tider:</strong> {selectedSlots.join(', ')}
              </div>
            )}

            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={handleOpenModal}
                disabled={submitting}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {submitting ? 'Skickar...' : 'Föreslå tider för hembesök'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal för att välja tider */}
      <MeetingBookingModal
        isOpen={showModal}
        onClose={handleCloseModal}
        meetingSlots={selectedSlots}
        onSlotChange={handleSlotChange}
        availableSlots={availableSlots}
        maxSlots={5}
        minSlots={2}
        mode="submit"
        onSubmit={handleSubmitSlots}
        submitting={submitting}
      />
    </>
  );
};

export default CustomerHomeVisitRequest;
